// Home (spec/15 P-01): the live session with its quick control, the wallet balances, the passes with time left, and
// what happened lately (visits and orders). Every figure is the player's own (`/v1/me/*`); an empty block says how
// to fill it.

import type { MyOrderSummary, MyPlaySessionEntry } from '@clavian/api-client';
import { useTranslation } from '@clavian/i18n/react';
import { EmptyState, PageHero, PageSkeleton, ProgressBar, Skeleton } from '@clavian/ui-web';
import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';

import { useLiveSession, useOrders, usePasses, usePlayHistory, useSession, useWallets } from '../api/queries';
import { formatterFor, shortDuration } from '../lib/format';
import { SectionHead } from '../shell/feedback';
import { NavIcon } from '../shell/nav-icons';
import { LiveSessionCard } from './live';

export function HomePage(): ReactNode {
  const { t } = useTranslation('player');
  const user = useSession().data?.user;
  const name = user?.name?.trim() || user?.username?.trim();
  // Nothing has arrived yet: the shape of the page, then the page (the blocks below load on their own after that).
  const live = useLiveSession();
  const wallets = useWallets();
  const passes = usePasses();
  if (live.isPending && wallets.isPending && passes.isPending) {
    return (
      <div className="pw-page pw-home">
        <PageSkeleton label={t('app.loading')} />
      </div>
    );
  }

  return (
    <div className="pw-page pw-home">
      <PageHero
        className="pw-home__hero"
        title={name ? t('home.greeting', { name }) : t('home.greetingPlain')}
        meta={t('home.intro')}
      />
      <LiveBlock />
      <div className="pw-home__grid">
        <WalletBlock />
        <PassesBlock />
      </div>
      <ActivityBlock />
    </div>
  );
}

function LiveBlock(): ReactNode {
  const { t } = useTranslation('player');
  const live = useLiveSession();
  if (live.isPending) return <Skeleton height={144} />;
  if (live.data?.live !== undefined) return <LiveSessionCard live={live.data.live} />;
  return (
    <section className="pw-idle" aria-labelledby="pw-idle-title">
      <span className="pw-idle__icon" aria-hidden="true">
        <NavIcon name="monitor" size={28} />
      </span>
      <div className="pw-idle__body">
        <h2 id="pw-idle-title" className="pw-idle__title">
          {t('home.notPlaying.title')}
        </h2>
        <p className="pw-muted">{t('home.notPlaying.body')}</p>
      </div>
      <Link to="/venues" className="pw-btn pw-btn--secondary">
        {t('home.notPlaying.find')}
      </Link>
    </section>
  );
}

function WalletBlock(): ReactNode {
  const { t } = useTranslation('player');
  const wallets = useWallets();
  const data = wallets.data?.data ?? [];
  return (
    <section className="pw-panel" aria-labelledby="pw-home-wallet">
      <SectionHead
        id="pw-home-wallet"
        title={t('home.wallets.title')}
        action={
          data.length === 0 ? undefined : (
            <Link to="/wallet" className="pw-link">
              {t('home.wallets.all')}
            </Link>
          )
        }
      />
      {wallets.isPending ? (
        <Skeleton shape="text" lines={2} />
      ) : data.length === 0 ? (
        <EmptyState size="compact" title={t('home.wallets.none')} description={t('home.wallets.noneBody')} />
      ) : (
        <ul className="pw-balances">
          {data.map((wallet) => (
            <li key={wallet.organization.id} className="pw-balance">
              <span className="pw-balance__org">{wallet.organization.name}</span>
              <span className="pw-balance__amount clav-num">{formatterFor(wallet.total.currency).money(wallet.total)}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function PassesBlock(): ReactNode {
  const { t } = useTranslation('player');
  const passes = usePasses();
  const active = passes.data?.active ?? [];
  return (
    <section className="pw-panel" aria-labelledby="pw-home-passes">
      <SectionHead
        id="pw-home-passes"
        title={t('home.passes.title')}
        action={
          <Link to="/passes" className="pw-link">
            {t('home.passes.all')}
          </Link>
        }
      />
      {passes.isPending ? (
        <Skeleton shape="text" lines={2} />
      ) : active.length === 0 ? (
        <EmptyState size="compact" title={t('home.passes.none')} description={t('home.passes.noneBody')} />
      ) : (
        <ul className="pw-minipasses">
          {active.slice(0, 3).map((pass) => (
            <li key={pass.id} className="pw-minipass">
              <span className="pw-minipass__top">
                <span className="pw-minipass__name">{pass.name}</span>
                <span className="pw-minipass__left clav-num">{t('home.passes.left', { duration: shortDuration(pass.remainingSeconds) })}</span>
              </span>
              <ProgressBar
                size="sm"
                value={pass.grantedSeconds === 0 ? 0 : pass.remainingSeconds / pass.grantedSeconds}
                label={t('home.passes.left', { duration: shortDuration(pass.remainingSeconds) })}
              />
              <span className="pw-subtle">{pass.organization.name}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

type Activity =
  | { readonly kind: 'visit'; readonly at: string; readonly entry: MyPlaySessionEntry }
  | { readonly kind: 'order'; readonly at: string; readonly order: MyOrderSummary };

function ActivityBlock(): ReactNode {
  const { t } = useTranslation('player');
  const visits = usePlayHistory(5);
  const orders = useOrders(5);
  const fmt = formatterFor('INR');
  const items: Activity[] = [
    ...(visits.data?.pages[0]?.data ?? []).map((entry): Activity => ({ kind: 'visit', at: entry.startedAt, entry })),
    ...(orders.data?.pages[0]?.data ?? []).map((order): Activity => ({ kind: 'order', at: order.placedAt, order })),
  ]
    .sort((a, b) => Date.parse(b.at) - Date.parse(a.at))
    .slice(0, 6);

  return (
    <section className="pw-panel" aria-labelledby="pw-home-activity">
      <SectionHead
        id="pw-home-activity"
        title={t('home.activity.title')}
        action={
          <span className="pw-section-head__links">
            <Link to="/play" className="pw-link">
              {t('home.activity.allVisits')}
            </Link>
            <Link to="/orders" className="pw-link">
              {t('home.activity.allOrders')}
            </Link>
          </span>
        }
      />
      {visits.isPending || orders.isPending ? (
        <Skeleton shape="text" lines={3} />
      ) : items.length === 0 ? (
        <EmptyState
          size="compact"
          title={t('home.activity.empty')}
          description={t('home.activity.emptyBody')}
          action={
            <Link to="/venues" className="pw-btn pw-btn--secondary">
              {t('home.notPlaying.find')}
            </Link>
          }
        />
      ) : (
        <ul className="pw-rows">
          {items.map((item) =>
            item.kind === 'visit' ? (
              <li key={`v-${item.entry.id}`} className="pw-row">
                <span className="pw-row__icon" aria-hidden="true">
                  <NavIcon name="monitor" size={20} />
                </span>
                <span className="pw-row__main">
                  <span className="pw-row__title">{t('home.activity.visit', { duration: shortDuration(item.entry.playedSeconds), station: item.entry.stationName })}</span>
                  <span className="pw-row__meta">
                    {item.entry.venue.name} · {fmt.relative(item.at)}
                  </span>
                </span>
              </li>
            ) : (
              <li key={`o-${item.order.id}`} className="pw-row pw-row--link">
                <span className="pw-row__icon" aria-hidden="true">
                  <NavIcon name="orders" size={20} />
                </span>
                <Link to="/orders/$saleId" params={{ saleId: item.order.id }} className="pw-row__main pw-row__link">
                  <span className="pw-row__title">{item.order.title || t('home.activity.order', { venue: item.order.venue.name })}</span>
                  <span className="pw-row__meta">
                    {item.order.venue.name} · {fmt.relative(item.at)}
                  </span>
                </Link>
                <span className="pw-row__end clav-num">{formatterFor(item.order.total.currency).money(item.order.total)}</span>
              </li>
            ),
          )}
        </ul>
      )}
    </section>
  );
}
